import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import FlashInfo from '../FlashInfo';
import { createPaymentCheckout } from '../../utils/paymentUtils';
import { Calendar, Clock, CheckCircle, ArrowLeft, Smartphone, CreditCard, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentBooking = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Date, 2: Heure, 3: Informations, 4: Paiement
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentChoice, setPaymentChoice] = useState(''); // 'online' or 'on_site'
  const [createdAppointmentId, setCreatedAppointmentId] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    service_type: '',
    device_info: '',
    notes: ''
  });

  const serviceTypes = [
    'Réparation écran',
    'Remplacement batterie',
    'Problème de charge',
    'Problème logiciel',
    'Réparation PC',
    'Diagnostic gratuit',
    'Autre'
  ];

  // Générer les prochains jours disponibles (exclure dimanche)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Exclure les dimanches
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long' 
          })
        });
      }
    }
    
    return dates;
  };

  const availableDates = getAvailableDates();

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments/available-slots?date=${date}`);
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Erreur lors du chargement des créneaux');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    fetchAvailableSlots(date);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const appointmentData = {
        ...formData,
        appointment_date: selectedTime
      };

      const response = await axios.post(`${API}/appointments`, appointmentData);
      
      toast.success('Rendez-vous créé !');
      
      // Store appointment ID and move to payment step
      setCreatedAppointmentId(response.data.id);
      setStep(4);
      
    } catch (error) {
      console.error('Error creating appointment:', error);
      if (error.response?.status === 400) {
        toast.error('Ce créneau n\'est plus disponible. Veuillez en choisir un autre.');
        setStep(2);
        fetchAvailableSlots(selectedDate);
      } else {
        toast.error('Erreur lors de la prise de rendez-vous');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentChoice = async (choice) => {
    setPaymentChoice(choice);
    
    if (choice === 'on_site') {
      // Direct confirmation without payment
      toast.success('Rendez-vous confirmé ! Paiement sur place. Vous recevrez une confirmation par email.');
      setTimeout(() => navigate('/'), 2000);
    } else if (choice === 'online') {
      // Initiate Stripe checkout for deposit (33%)
      try {
        await createPaymentCheckout({
          payment_type: 'appointment_deposit',
          appointment_id: createdAppointmentId,
          customer_name: formData.client_name,
          customer_email: formData.client_email,
          customer_phone: formData.client_phone,
          metadata: {
            service_type: formData.service_type,
            appointment_date: selectedTime
          }
        });
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Erreur lors de l\'initialisation du paiement');
      }
    }
  };

  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg">
              <Calendar className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Prendre Rendez-vous
          </h1>
          <p className="text-lg text-gray-600">
            Réservez un créneau pour votre réparation
          </p>
        </div>

        {/* Flash Info */}
        <FlashInfo pageName="appointments" />

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <CheckCircle className="w-6 h-6" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Choisissez une Date
              </CardTitle>
              <CardDescription>
                Sélectionnez le jour de votre rendez-vous
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableDates.map((date) => (
                  <Button
                    key={date.value}
                    onClick={() => handleDateSelect(date.value)}
                    variant="outline"
                    className="h-auto py-4 px-4 text-left justify-start hover:bg-blue-50 hover:border-blue-600"
                  >
                    <div>
                      <p className="font-semibold capitalize">{date.label}</p>
                      <p className="text-xs text-gray-500">Créneaux disponibles</p>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Choisissez l'Heure
              </CardTitle>
              <CardDescription>
                Créneaux disponibles le {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="mb-4"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Changer de date
              </Button>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement des créneaux...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun créneau disponible pour cette date</p>
                  <Button onClick={() => setStep(1)} className="mt-4">
                    Choisir une autre date
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot}
                      onClick={() => handleTimeSelect(slot)}
                      variant="outline"
                      className="h-auto py-3 hover:bg-blue-50 hover:border-blue-600"
                    >
                      {formatTime(slot)}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Information Form */}
        {step === 3 && (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Vos Informations
              </CardTitle>
              <CardDescription>
                Rendez-vous le {new Date(selectedTime).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {formatTime(selectedTime)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className="mb-4"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Changer l'heure
              </Button>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="client_name">Nom complet *</Label>
                  <Input
                    id="client_name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <Label htmlFor="client_phone">Téléphone *</Label>
                  <Input
                    id="client_phone"
                    name="client_phone"
                    type="tel"
                    value={formData.client_phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+32 475 12 34 56"
                  />
                </div>

                <div>
                  <Label htmlFor="client_email">Email</Label>
                  <Input
                    id="client_email"
                    name="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={handleInputChange}
                    placeholder="jean.dupont@email.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Pour recevoir la confirmation par email
                  </p>
                </div>

                <div>
                  <Label htmlFor="service_type">Type de service *</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="device_info">Appareil concerné</Label>
                  <Input
                    id="device_info"
                    name="device_info"
                    value={formData.device_info}
                    onChange={handleInputChange}
                    placeholder="Ex: iPhone 14 Pro, Samsung Galaxy S23"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes / Détails du problème</Label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Décrivez brièvement le problème..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full"
                  size="lg"
                >
                  {submitting ? 'Confirmation en cours...' : 'Confirmer le Rendez-vous'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}


        {/* Step 4: Payment Choice */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Étape 4 : Mode de paiement
              </CardTitle>
              <CardDescription>
                Choisissez comment vous souhaitez payer votre acompte (33% du montant estimé)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payment summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Date :</span>
                    <span className="font-medium">
                      {new Date(selectedTime).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Heure :</span>
                    <span className="font-medium">{formatTime(selectedTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Service :</span>
                    <span className="font-medium">{formData.service_type}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-300">
                    <span className="font-semibold">Acompte à payer (33%) :</span>
                    <span className="font-bold text-blue-700">
                      {formData.estimated_price ? 
                        Math.max(Math.round(formData.estimated_price * 0.33), 10).toFixed(2) + '€' :
                        'À partir de 10€'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment options */}
              <div className="space-y-3">
                <Button
                  onClick={() => handlePaymentChoice('online')}
                  className="w-full h-auto py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  disabled={submitting}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <CreditCard className="w-6 h-6 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold">Payer l'acompte en ligne</div>
                        <div className="text-xs opacity-90">Carte bancaire, Apple Pay, Google Pay</div>
                      </div>
                    </div>
                    <div className="text-sm">
                      Sécurisé 🔒
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={() => handlePaymentChoice('on_site')}
                  variant="outline"
                  className="w-full h-auto py-4"
                  disabled={submitting}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <Store className="w-6 h-6 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold">Payer sur place</div>
                        <div className="text-xs text-slate-500">Paiement lors de votre rendez-vous</div>
                      </div>
                    </div>
                  </div>
                </Button>
              </div>

              <p className="text-xs text-center text-slate-500">
                En confirmant, vous acceptez nos conditions générales de vente
              </p>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default AppointmentBooking;
